import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaVenusMars,
  FaBriefcase,
  FaGraduationCap,
  FaGavel,
  FaCreditCard,
  FaHome,
  FaCity,
  FaImage,
} from "react-icons/fa";

const AdvocateProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);

      // First try to get from localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const parsed = JSON.parse(userStr);
          const userInfo = parsed.user || parsed;
          if (userInfo && userInfo.id) {
            // Try to fetch full profile from API
            try {
              const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/advocate/profile`,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );
              if (response.data.success || response.data.data) {
                setProfileData(response.data.data || response.data);
                setLoading(false);
                return;
              }
            } catch (apiError) {
              console.log("API fetch failed, using localStorage data:", apiError);
              // Fall back to localStorage data
              setProfileData(userInfo);
              setLoading(false);
              return;
            }
          }
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
        }
      }

      // If no data found, show error
      toast.error("Unable to load profile data");
      setLoading(false);
    } catch (error) {
      console.error("Error fetching profile data:", error);
      toast.error("Failed to load profile");
      setLoading(false);
    }
  };

  const ProfileField = ({ icon: Icon, label, value, placeholder = "Not provided" }) => (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <label className="block text-gray-600 text-sm font-medium mb-2">
        <div className="flex items-center gap-2">
          <Icon className="text-orange-500" />
          {label}
        </div>
      </label>
      <p className="text-gray-800 font-semibold text-base">
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-red-800 mb-2">No Profile Data Found</h2>
          <p className="text-red-600">Unable to load your profile information. Please try logging in again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <ToastContainer />

      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Your Profile</h1>
        <p className="text-sm sm:text-base text-gray-600">View your complete advocate profile information</p>
      </div>

      {/* Profile Image and Basic Info Card */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Profile Image */}
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-orange-200 shadow-lg">
              {profileData.profileImage ? (
                <img
                  src={profileData.profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <FaUser className="text-white text-5xl" />
                </div>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {profileData.name || "Advocate"}
            </h2>
            <div className="space-y-2">
              <p className="text-gray-600">
                <span className="font-semibold">Advocate ID:</span> {profileData.advocateId || profileData.uniqueId || "N/A"}
              </p>
              {profileData.specialization && (
                <p className="text-gray-600">
                  <span className="font-semibold">Specialization:</span> {profileData.specialization}
                </p>
              )}
              {profileData.experience && (
                <p className="text-gray-600">
                  <span className="font-semibold">Experience:</span> {profileData.experience} {profileData.experience === 1 ? 'Year' : 'Years'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField
            icon={FaUser}
            label="Full Name"
            value={profileData.name}
          />
          <ProfileField
            icon={FaVenusMars}
            label="Gender"
            value={profileData.gender ? profileData.gender.charAt(0).toUpperCase() + profileData.gender.slice(1) : null}
          />
          <ProfileField
            icon={FaEnvelope}
            label="Email Address"
            value={profileData.email}
          />
          <ProfileField
            icon={FaPhone}
            label="Phone Number"
            value={profileData.phoneNo}
          />
          <ProfileField
            icon={FaCalendarAlt}
            label="Date of Birth"
            value={profileData.dateOfBirth ? new Date(profileData.dateOfBirth).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : null}
          />
        </div>
      </div>

      {/* Legal Documents Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
          Legal Documents
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField
            icon={FaIdCard}
            label="Aadhar Number"
            value={profileData.aadharNo ? `${profileData.aadharNo.slice(0, 4)}****${profileData.aadharNo.slice(-4)}` : null}
          />
          <ProfileField
            icon={FaCreditCard}
            label="PAN Card"
            value={profileData.pancard}
          />
          <ProfileField
            icon={FaIdCard}
            label="Unique ID (Advocate)"
            value={profileData.uniqueId}
          />
          <ProfileField
            icon={FaGavel}
            label="Advocate ID"
            value={profileData.advocateId}
          />
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
          Professional Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField
            icon={FaBriefcase}
            label="Specialization"
            value={profileData.specialization}
          />
          <ProfileField
            icon={FaGraduationCap}
            label="Experience"
            value={profileData.experience ? `${profileData.experience} ${profileData.experience === 1 ? 'Year' : 'Years'}` : null}
          />
          <ProfileField
            icon={FaGavel}
            label="Bar Association/Court"
            value={profileData.barAssociationCourt}
          />
        </div>
      </div>

      {/* Address Information Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
          Address Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <ProfileField
              icon={FaHome}
              label="Residence Address"
              value={profileData.residenceAddress || profileData.residenceaddress}
            />
          </div>
          <ProfileField
            icon={FaMapMarkerAlt}
            label="State"
            value={profileData.state}
          />
          <ProfileField
            icon={FaCity}
            label="City"
            value={profileData.city}
          />
          <ProfileField
            icon={FaIdCard}
            label="Pin Code"
            value={profileData.pincode}
          />
        </div>
      </div>

      {/* Account Information Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
          Account Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileField
            icon={FaUser}
            label="Account Status"
            value={profileData.isVerified ? "Verified" : "Pending Verification"}
          />
          {profileData.createdAt && (
            <ProfileField
              icon={FaCalendarAlt}
              label="Member Since"
              value={new Date(profileData.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvocateProfile;

